// Base layout for a Gamzone game. Mirrors the reference games' App.jsx:
//
//  Desktop (>899px): a portrait game surface on the left
//  (.canvas-side/.canvas-stack) and the shared info panel on the right
//  (.info-side) — header · controls legend · help · point-policy ·
//  account-summary · CTA · ad slot.
//
//  Mobile (≤899px / coarse pointer): the canvas goes fullscreen and the info
//  panel becomes a centered modal (.info-side.modal) over a backdrop. A
//  hamburger (.standby-menu-btn) re-opens it.
//
// Nothing here is game-specific: content comes from src/app/gameConfig.ts, the
// game itself is src/game/Game.tsx.

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useT, useLang } from "../gamzone/i18n.ts";
import { useAuth } from "../gamzone/auth/AuthContext.tsx";
import { useIsMobile } from "../gamzone/useIsMobile.ts";
import Settings from "../gamzone/ui/Settings.tsx";
import { usePoints, INITIAL_POINTS } from "../gamzone/points.ts";
import { VERSUS_HOST_COST, type Session } from "../gamzone/net/peer.ts";
import { Game } from "../game/Game.tsx";
import Lobby from "../game/friend/Lobby.tsx";
import { FriendMatch } from "../game/friend/FriendMatch.tsx";
import { AdSlot } from "../gamzone/ads/AdSlot.tsx";
import {
  gameConfig,
  DEV,
  OPTIONS_EVENT,
  CTA_EVENT,
  RESET_EVENT,
  SKIP_EVENT,
  LOSE_EVENT,
  DRAW_EVENT,
} from "./gameConfig.ts";

export function App() {
  const t = useT();
  const lang = useLang();
  const auth = useAuth();
  const points = usePoints();
  const isMobile = useIsMobile();

  const [showSettings, setShowSettings] = useState(false);
  const [policyOpen, setPolicyOpen] = useState(false);
  // On mobile the panel starts open as the pre-game modal; desktop ignores it.
  const [menuOpen, setMenuOpen] = useState(true);

  // 친구랑 같이하기 — a connected Session (see gamzone/net/peer.ts) swaps the
  // campaign <Game/> for <FriendMatch/> in the canvas; showLobby is the
  // host/join modal shown up to that point.
  const [showLobby, setShowLobby] = useState(false);
  const [friendSession, setFriendSession] = useState<Session | null>(null);

  // The map/dialogue/subtitle/ending screens have no sidebar-independent
  // settings access of their own (unlike MatchScreen's on-canvas pause
  // button) — their ⚙ button (OptionsButton.tsx) fires this instead of
  // needing a prop threaded through every Game.tsx scene branch.
  useEffect(() => {
    const open = () => setShowSettings(true);
    window.addEventListener(OPTIONS_EVENT, open);
    return () => window.removeEventListener(OPTIONS_EVENT, open);
  }, []);

  const accountName = auth.user
    ? auth.user.displayName || auth.user.email
    : auth.configured
      ? t("signInCta")
      : t("loginUnconfigured");

  const c = gameConfig.controls;
  const panelIsModal = isMobile && menuOpen;
  const showHamburger = isMobile && !menuOpen && !showSettings;

  const panelBody = (
    <>
      <header className="info-header">
        <h1>{gameConfig.title}</h1>
        <p className="subtitle">{gameConfig.subtitle}</p>
      </header>

      {c.show && (
        <section
          className={`controls${c.dpad ? "" : " no-dpad"}`}
          aria-hidden="true"
        >
          {/* The d-pad glyphs are always shown (control-legend chrome, same as
             every other Gamzone game). Only their *click* behaviour is
             DEV-only: outside `?dev` these are inert legend squares; with
             `?dev` clicking ◀▶▼ force-ends the match (loss/win/draw) so each
             result screen is easy to check without playing it out. */}
          {c.dpad && (
            <div className="dpad">
              <div className="control-button move-button up">{c.dpadGlyphs[0]}</div>
              <div
                className="control-button move-button left"
                style={DEV ? { cursor: "pointer" } : undefined}
                onClick={
                  DEV
                    ? () => window.dispatchEvent(new Event(LOSE_EVENT))
                    : undefined
                }
              >
                {c.dpadGlyphs[1]}
              </div>
              <div
                className="control-button move-button right"
                style={DEV ? { cursor: "pointer" } : undefined}
                onClick={
                  DEV
                    ? () => window.dispatchEvent(new Event(SKIP_EVENT))
                    : undefined
                }
              >
                {c.dpadGlyphs[2]}
              </div>
              <div
                className="control-button move-button down"
                style={DEV ? { cursor: "pointer" } : undefined}
                onClick={
                  DEV
                    ? () => window.dispatchEvent(new Event(DRAW_EVENT))
                    : undefined
                }
              >
                {c.dpadGlyphs[3]}
              </div>
            </div>
          )}
          {c.actionLabel(lang).length > 0 && (
            <div className="action-stack">
              {c.actionLabel(lang).slice(0, 2).map((label, i) => (
                <div
                  key={i}
                  className={`control-button ${i === 0 ? "grab-button" : "throw-button"}`}
                >
                  {label}
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      <section
        className="help"
        dangerouslySetInnerHTML={{ __html: gameConfig.helpHtml(isMobile, lang) }}
      />

      <section className="help points-policy">
        <button
          type="button"
          className="points-policy-toggle"
          aria-expanded={policyOpen}
          onClick={() => setPolicyOpen((o) => !o)}
        >
          <span>{t("pointPolicy")}</span>
          <span className="points-policy-chevron" aria-hidden="true" />
        </button>
        {policyOpen && (
          <div className="points-policy-body">
            <p>{t("welcomePoints", { points: INITIAL_POINTS })}</p>
            {gameConfig.rewardLine && (
              <p>
                {gameConfig.rewardLine.replace(
                  "{points}",
                  String(gameConfig.rewardPoints),
                )}
              </p>
            )}
            <p>{t("guestPlay")}</p>
            <p>{t("cloudPreferred")}</p>
            <p>{t("lowPointsGuard")}</p>
          </div>
        )}
      </section>

      <button
        type="button"
        className="account-summary account-summary--button"
        onClick={() => setShowSettings(true)}
        aria-label={t("account")}
      >
        <strong className="account-summary-id">{accountName}</strong>
        <span className="account-summary-points">{points.toLocaleString()} P</span>
      </button>

      {!friendSession && (
        <button
          type="button"
          className="lobby-open"
          onClick={() => setShowLobby(true)}
        >
          {t("versus", { points: VERSUS_HOST_COST })}
        </button>
      )}

      {gameConfig.ctaLabel(lang) && (
        <button
          type="button"
          className="lobby-open"
          onClick={() => window.dispatchEvent(new Event(CTA_EVENT))}
        >
          {gameConfig.ctaLabel(lang)}
        </button>
      )}

      {/* DEV-only (gameConfig.resetLabel is null without ?dev): replay from
         the very start (막 I prologue). */}
      {gameConfig.resetLabel(lang) && (
        <button
          type="button"
          className="lobby-open"
          style={{ marginTop: 4, opacity: 0.7, fontSize: "0.82rem", letterSpacing: 0 }}
          onClick={() => window.dispatchEvent(new Event(RESET_EVENT))}
        >
          {gameConfig.resetLabel(lang)}
        </button>
      )}

      {panelIsModal && (
        <button
          type="button"
          className="start-button"
          onClick={() => setMenuOpen(false)}
        >
          {t("play")}
        </button>
      )}

      {gameConfig.showAdSlot && <AdSlot className="panel-ad-slot panel-ad-slot--pc" />}
    </>
  );

  return (
    <main className="layout">
      <section className="canvas-side">
        <div className="canvas-stack">
          {friendSession ? (
            <FriendMatch
              session={friendSession}
              onExit={() => setFriendSession(null)}
            />
          ) : (
            <Game />
          )}
          {showHamburger && (
            <button
              type="button"
              className="standby-menu-btn"
              aria-label={t("options")}
              onClick={() => setMenuOpen(true)}
            >
              <span />
              <span />
              <span />
            </button>
          )}
        </div>
      </section>

      {/* Desktop: always-on right column. Mobile: centered modal + backdrop. */}
      {!isMobile && <aside className="info-side">{panelBody}</aside>}
      {panelIsModal && (
        <>
          <div
            className="modal-backdrop info-modal-backdrop"
            aria-hidden="true"
            onClick={() => setMenuOpen(false)}
          />
          <aside className="info-side modal">{panelBody}</aside>
        </>
      )}

      {showSettings &&
        createPortal(
          <div className="modal-backdrop" onClick={() => setShowSettings(false)}>
            <div className="modal-wrap" onClick={(e) => e.stopPropagation()}>
              <Settings onClose={() => setShowSettings(false)} />
            </div>
          </div>,
          document.body,
        )}

      {showLobby &&
        createPortal(
          <div className="modal-backdrop" onClick={() => setShowLobby(false)}>
            <div className="modal-wrap" onClick={(e) => e.stopPropagation()}>
              <Lobby
                points={points}
                onConnected={(session) => {
                  setShowLobby(false);
                  setMenuOpen(false);
                  setFriendSession(session);
                }}
                onCancel={() => setShowLobby(false)}
              />
            </div>
          </div>,
          document.body,
        )}
    </main>
  );
}
